import cx from 'classnames';
import Icon from 'components/Icon';
import { isString, memoize } from 'lodash';
import React from 'react';
import {
  branch,
  compose,
  defaultProps,
  withProps,
  withPropsOnChange,
} from 'recompose';
import withRemoteOptions from './withRemoteOptions';

import createLabel from '../createLabel';
import withField from '../withField';
import { Autocomplete } from './Autocomplete';

import styles from './styles.css';

export type Props = {
  disabled?: boolean;
  inline?: boolean;
  hideArrow?: boolean;
  type?: string;
  error?: string;
  label?: string;
  autoFocus?: boolean;
  defaultValue?: string;
  value?: string | number;
  onFocus?: (FormEvent) => void;
  onBlur?: (FormEvent) => void;
  onChange?: (FormEvent) => void;
  theme?: 'normal' | 'rounded' | 'dark';
  size?: 'small' | 'medium' | 'large';
  style?: any;
  icon?: string;
  placeholder?: string;
  onEnter?: () => void;
  onChangeSearch?: () => void;
  multiple?: boolean;
  searchable?: boolean;
  allowCreate?: boolean;
  options?: any[] | { [key: string]: any };
  parse?: () => void;
  normalize?: () => void;
  showAddButton?: boolean;
  itemRenderer?: () => void;
  ignoreSearch?: boolean;
  className?: string;
  stateless?: boolean;
  allowEmpty?: boolean;
};

export type OwnProps = Props & {
  theme: string;
  size: string;
  icon: JSX.Element | undefined;
};

const getIcon = memoize(
  (name) =>
    name && <Icon name={name} className={styles.icon} width={24} height={24} />
);

export const Select = compose<OwnProps, Props>(
  defaultProps({ theme: 'normal', size: 'medium' }),
  withProps(({ icon }) => ({ icon: getIcon(icon) })),
  withPropsOnChange(
    ['label'],
    ({ label, input }) =>
      !!label && {
        label: React.createElement(
          isString(label) ? createLabel(label) : label,
          { className: styles.label, htmlFor: input && input.name }
        ),
      }
  ),
  branch(({ fetch }) => !!fetch, withRemoteOptions)
)(
  ({
    disabled,
    type,
    error,
    label,
    defaultValue,
    value,
    placeholder,
    options,
    multiple,
    searchable,
    allowCreate,
    autoFocus,
    inline,
    hideArrow,
    allowEmpty,

    onChangeSearch,
    onChange,
    onCreate,
    onFocus,

    theme,
    size,
    style,
    icon,
    showAddButton,
    itemRenderer,
    ignoreSearch,
    className,
    stateless,
    getLabel,
  }) => {
    return (
      <div
        style={style}
        className={cx(
          styles.root,
          styles[`theme-${theme}`],
          styles[`size-${size}`],
          error && styles.hasError,
          !searchable && disabled && styles.disabled,
          inline && styles.inline,
          className
        )}
      >
        {label}
        <div className={styles.wrapper}>
          <Autocomplete
            onChangeSearch={onChangeSearch}
            autoFocus={autoFocus}
            icon={icon}
            hideArrow={hideArrow}
            multiple={multiple}
            searchable={searchable}
            allowCreate={allowCreate}
            disabled={disabled}
            onChange={onChange}
            onCreate={onCreate}
            placeholder={placeholder}
            defaultValue={value || defaultValue}
            options={options}
            showAddButton={showAddButton}
            value={value}
            itemRenderer={itemRenderer}
            ignoreSearch={ignoreSearch}
            stateless={stateless}
            allowEmpty={allowEmpty}
            getLabel={getLabel}
          />
        </div>
        {error && typeof error === 'string' && (
          <div className={styles.error}>{error}</div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default withField(Select);
